import { FileText, Plus, Send, Eye, CheckCircle, Clock, X, Sparkles, Download, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useProposals, useUpdateProposal, useDeleteProposal } from "@/hooks/useProposals";
import { AddProposalDialog } from "@/components/proposals/AddProposalDialog";
import { Skeleton } from "@/components/ui/skeleton";
import { format } from "date-fns";
import { toast } from "sonner";

const statusConfig = {
  draft: { label: "Draft", icon: FileText, className: "bg-muted text-muted-foreground" },
  sent: { label: "Sent", icon: Send, className: "bg-blue-500/10 text-blue-400" },
  viewed: { label: "Viewed", icon: Eye, className: "bg-purple-500/10 text-purple-400" },
  accepted: { label: "Accepted", icon: CheckCircle, className: "bg-success/10 text-success" },
  rejected: { label: "Rejected", icon: X, className: "bg-destructive/10 text-destructive" },
  expired: { label: "Expired", icon: Clock, className: "bg-warning/10 text-warning" },
  signed: { label: "Signed", icon: CheckCircle, className: "bg-success/10 text-success" },
};

export default function Proposals() {
  const { data: proposals, isLoading } = useProposals();
  const updateProposal = useUpdateProposal();
  const deleteProposal = useDeleteProposal();

  const handleSend = (id: string) => {
    updateProposal.mutate({ id, status: "sent", sent_at: new Date().toISOString() });
  };

  const formatCurrency = (amount: number | null, currency: string | null) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD",
    }).format(amount || 0);
  };

  if (isLoading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Proposals & Contracts</h1>
            <p className="text-muted-foreground">Generate and manage your sales documents</p>
          </div>
        </div>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      </div>
    );
  }

  const draftProposals = proposals?.filter((p) => p.status === "draft") || [];
  const sentProposals = proposals?.filter((p) => p.status !== "draft") || [];

  return (
    <div className="space-y-6 animate-fade-in" data-wizard="proposals">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Proposals & Contracts</h1>
          <p className="text-muted-foreground">
            Generate and manage your sales documents
          </p>
        </div>
        <AddProposalDialog />
      </div>

      {/* AI Generation Card */}
      <div className="glass rounded-xl p-5 gradient-border">
        <div className="flex items-start gap-4">
          <div className="p-2 rounded-lg bg-primary/10 ai-glow-subtle">
            <Sparkles className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-foreground">
              AI Proposal Generation
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Generate professional proposals automatically based on deal
              details, product configurations, and customer needs. Our AI
              creates compelling content tailored to each prospect.
            </p>
          </div>
          <Button 
            variant="outline" 
            className="gap-2"
            onClick={() => toast.info("AI Proposal Generation", { 
              description: "Select a deal from the Deals page to generate a proposal" 
            })}
          >
            <Sparkles className="h-4 w-4" />
            Generate
          </Button>
        </div>
      </div>

      {/* Active Proposals Section */}
      <div className="glass rounded-xl overflow-hidden">
        <div className="p-5 border-b border-border/50 flex items-center justify-between">
          <h2 className="font-semibold">Active Proposals</h2>
          <span className="text-sm text-muted-foreground">
            {sentProposals.length} sent
          </span>
        </div>
        <div className="divide-y divide-border/50">
          {sentProposals.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              No sent proposals yet. Create and send your first proposal.
            </div>
          ) : (
            sentProposals.map((proposal) => {
              const status = statusConfig[proposal.status as keyof typeof statusConfig] || statusConfig.draft;
              const StatusIcon = status.icon;

              return (
                <div
                  key={proposal.id}
                  className="p-5 hover:bg-muted/30 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className="p-2 rounded-lg bg-muted">
                        <FileText className="h-5 w-5 text-muted-foreground" />
                      </div>
                      <div>
                        <h3 className="font-medium">{proposal.title}</h3>
                        <p className="text-sm text-muted-foreground">
                          {proposal.deals?.title || "No deal"} • {proposal.contacts ? `${proposal.contacts.first_name} ${proposal.contacts.last_name}` : "No contact"}
                        </p>
                        {proposal.sent_at && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Sent: {format(new Date(proposal.sent_at), "MMM d, yyyy h:mm a")}
                            {proposal.viewed_at && ` • Viewed: ${format(new Date(proposal.viewed_at), "MMM d, yyyy h:mm a")}`}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="font-medium text-success">
                        {formatCurrency(proposal.total_amount, proposal.currency)}
                      </span>
                      <span className={cn("stage-pill flex items-center gap-1", status.className)}>
                        <StatusIcon className="h-3 w-3" />
                        {status.label}
                      </span>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <Download className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Draft Proposals Section */}
      <div className="glass rounded-xl overflow-hidden">
        <div className="p-5 border-b border-border/50 flex items-center justify-between">
          <h2 className="font-semibold">Drafts</h2>
          <span className="text-sm text-muted-foreground">
            {draftProposals.length} drafts
          </span>
        </div>
        <div className="divide-y divide-border/50">
          {draftProposals.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              No draft proposals. Click "New Proposal" to create one.
            </div>
          ) : (
            draftProposals.map((proposal) => {
              const status = statusConfig.draft;
              const StatusIcon = status.icon;

              return (
                <div
                  key={proposal.id}
                  className="p-5 hover:bg-muted/30 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className="p-2 rounded-lg bg-muted">
                        <FileText className="h-5 w-5 text-muted-foreground" />
                      </div>
                      <div>
                        <h3 className="font-medium">{proposal.title}</h3>
                        <p className="text-sm text-muted-foreground">
                          {proposal.deals?.title || "No deal"} • {proposal.contacts ? `${proposal.contacts.first_name} ${proposal.contacts.last_name}` : "No contact"}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Created: {format(new Date(proposal.created_at), "MMM d, yyyy")}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="font-medium text-success">
                        {formatCurrency(proposal.total_amount, proposal.currency)}
                      </span>
                      <span className={cn("stage-pill flex items-center gap-1", status.className)}>
                        <StatusIcon className="h-3 w-3" />
                        {status.label}
                      </span>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleSend(proposal.id)}
                          disabled={updateProposal.isPending}
                        >
                          {updateProposal.isPending ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Send className="h-4 w-4" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          onClick={() => deleteProposal.mutate(proposal.id)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}